import { useEffect, useCallback, useRef, useState } from 'react';
import { useAppDispatch, authService, type SocialLoginRequest, getUserProfile, saveAccessToken, saveRefreshToken, getAccessToken } from '@ideoz/core';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface UseGoogleOneTapProps {
    clientId: string;
    onSuccess?: (user: any) => void;
    onError?: (error: any) => void;
    disabled?: boolean;
}

// Google Identity Services types for One Tap
interface GoogleAccountsId {
    initialize: (config: any) => void;
    prompt: (callback?: (notification: any) => void) => void;
    cancel: () => void;
}

// Type guard to check if Google One Tap API is available
function isGoogleOneTapAvailable(): boolean {
    return !!(window as any).google?.accounts?.id;
}

// Helper to get Google One Tap API
function getGoogleOneTap(): GoogleAccountsId | undefined {
    return (window as any).google?.accounts?.id;
}

/**
 * Decode JWT token to extract user information
 */
function parseJwt(token: string): any {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch (error) {
        console.error('Failed to parse JWT:', error);
        return null;
    }
}

/**
 * Load Google Identity Services script
 */
function loadGoogleIdentityScript(): Promise<void> {
    return new Promise((resolve, reject) => {
        // Check if already loaded
        if (isGoogleOneTapAvailable()) {
            resolve();
            return;
        }

        // Check if script is already being loaded
        const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
        if (existingScript) {
            // Wait for it to load
            existingScript.addEventListener('load', () => resolve());
            existingScript.addEventListener('error', () => reject(new Error('Failed to load Google Identity Services')));
            return;
        }

        // Load the script
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => {
            // Wait a bit for the API to be fully available
            const checkApi = () => {
                if (isGoogleOneTapAvailable()) {
                    resolve();
                } else {
                    setTimeout(checkApi, 50);
                }
            };
            checkApi();
        };
        script.onerror = () => {
            reject(new Error('Failed to load Google Identity Services script'));
        };
        document.head.appendChild(script);
    });
}

export function useGoogleOneTap({
    clientId,
    onSuccess,
    onError,
    disabled = false,
}: UseGoogleOneTapProps) {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const initializedRef = useRef(false);
    const scriptLoadedRef = useRef(false);
    const [isAvailable, setIsAvailable] = useState(false);

    const handleCredentialResponse = useCallback(
        async (response: any) => {
            try {
                console.log('Google One Tap: Credential received', response);

                // Check for errors in the response
                if (response?.error) {
                    const errorMessage = response.error || 'Authentication failed';
                    console.error('Google One Tap error:', errorMessage);
                    throw new Error(`Google authentication error: ${errorMessage}`);
                }

                // Handle both response.credential and direct credential
                const credential = response?.credential || response;
                
                if (!credential || typeof credential !== 'string') {
                    console.error('Invalid credential format:', { response, credential });
                    throw new Error('Invalid credential format received from Google');
                }

                // Decode the JWT token (ID token) to get user info
                const userInfo = parseJwt(credential);
                if (!userInfo || !userInfo.email) {
                    throw new Error('Failed to parse credential or missing user information');
                }

                console.log('Google One Tap: User authenticated', userInfo.email);

                // Get pairing_key from URL if present (for Figma plugin authentication)
                const pairingKey = searchParams.get('pairing_key');
                const figmaSource = searchParams.get('figma_source');

                // Call backend social login API with the ID token
                const socialLoginData: SocialLoginRequest = {
                    id_token: credential, // Google ID token (JWT)
                    platform: pairingKey ? 'plugin' : 'web',
                };

                // Add pairing_key if present
                if (pairingKey) {
                    socialLoginData.pairing_key = pairingKey;
                }

                console.log('Calling backend social login API with ID token...', { email: userInfo.email, hasPairingKey: !!pairingKey });
                
                const loginResponse = await authService.socialLogin(socialLoginData);

                if (!loginResponse.access_token) {
                    console.error('Invalid login response:', loginResponse);
                    throw new Error(loginResponse.message || 'Invalid response from server');
                }

                saveAccessToken(loginResponse.access_token);
                saveRefreshToken(loginResponse.refresh_token);
                (window as any).appAccessToken = loginResponse.access_token;
                const accessToken = loginResponse.access_token;

                // Get user profile first to update Redux state
                const userData = await dispatch(getUserProfile(accessToken)).unwrap();

                // Check if coming from Figma plugin - redirect to pairing page
                if (pairingKey) {
                    console.log('Google One Tap: Redirecting to pairing page with pairing_key');
                    navigate(`/redirect?pairing_key=${pairingKey}&figma_source=${figmaSource || 'desktop'}`);
                    onSuccess?.(userInfo);
                    return;
                }

                // Check onboarding status
                const pathname = window.location.pathname;
                const isOnLandingPage = pathname === '/' || pathname === '/landing';
                const isOnLoginOrRegisterPage = pathname === '/login' || pathname === '/register';
                const isOnBoardingShownRaw =
                    userData?.metadata?.isOnBoardingShown ?? userData?.metadata?.is_onboarding_shown ?? false;
                const isOnBoardingShown =
                    typeof isOnBoardingShownRaw === 'string'
                        ? isOnBoardingShownRaw.toLowerCase() === 'true'
                        : Boolean(isOnBoardingShownRaw);

                // Navigate to onboarding if needed
                if (isOnBoardingShown === false && isOnLandingPage) {
                    navigate('/onboarding');
                } else if (isOnLoginOrRegisterPage) {
                    // On login/register page - navigate to home and let the page's 
                    // useEffect handle proper redirect based on isLoggedIn state
                    navigate('/');
                } else if (!isOnLandingPage && isOnBoardingShown === false) {
                    authService.updateOnboardingShown().catch((err) => {
                        console.error('Failed to update onboarding flag:', err);
                    });
                }

                onSuccess?.(userInfo);
                console.log('Google One Tap sign-in successful');
            } catch (error: any) {
                console.error('Google One Tap failed:', error);
                
                // Provide more detailed error messages
                let errorMessage = 'Failed to sign in with Google. Please try again.';
                
                if (error?.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error?.message) {
                    errorMessage = error.message;
                } else if (typeof error === 'string') {
                    errorMessage = error;
                }
                
                console.error('Error details:', {
                    message: errorMessage,
                    error: error,
                    response: error?.response,
                });
                
                onError?.(new Error(errorMessage));
            }
        },
        [dispatch, navigate, onSuccess, onError, searchParams]
    );

    useEffect(() => {
        if (disabled || initializedRef.current) {
            return;
        }

        // Validate client ID
        if (!clientId || clientId.includes('your-google-client-id')) {
            console.warn('Google One Tap: Invalid or missing client ID');
            return;
        }

        // Check if user is already authenticated
        const token = getAccessToken();
        if (token) {
            console.log('User already authenticated, skipping One Tap');
            return;
        }

        // Load script and initialize
        const initializeOneTap = async () => {
            try {
                // Load Google Identity Services script if not already loaded
                if (!scriptLoadedRef.current) {
                    await loadGoogleIdentityScript();
                    scriptLoadedRef.current = true;
                }

                const googleOneTap = getGoogleOneTap();
                if (!googleOneTap) {
                    console.error('Google Identity Services API not available');
                    return;
                }

                setIsAvailable(true);

                // Check if FedCM is supported
                const fedcmSupported = 'IdentityCredential' in window;
                console.log('FedCM support:', fedcmSupported);

                console.log('Initializing Google One Tap...', { 
                    clientId,
                    origin: window.location.origin,
                    href: window.location.href,
                    fedcmSupported,
                });

                // Initialize One Tap with proper configuration
                // Try with FedCM first (to avoid warnings)
                console.log('Initializing One Tap with FedCM...');
                googleOneTap.initialize({
                    client_id: clientId,
                    callback: handleCredentialResponse,
                    auto_select: false, // Don't auto-select to show account picker
                    cancel_on_tap_outside: true,
                    use_fedcm_for_prompt: true, // Opt-in to FedCM to address the warning
                });

                // Wait a bit for initialization to complete
                await new Promise(resolve => setTimeout(resolve, 100));

                console.log('Calling googleOneTap.prompt()...');

                // Show the One Tap prompt
                // Note: With FedCM enabled, display moment notifications are not available
                // Only dismissed moment notifications are supported
                let promptCallbackCalled = false;
                
                googleOneTap.prompt((notification: any) => {
                    promptCallbackCalled = true;
                    console.log('One Tap prompt callback received:', notification);
                    
                    // With FedCM, we can only check for dismissed moments
                    if (notification?.isDismissedMoment && notification.isDismissedMoment()) {
                        const reason = notification.getDismissedMomentReason?.();
                        console.log('One Tap dismissed:', reason || 'User dismissed');
                    }
                    
                    // Log all available properties for debugging
                    if (notification) {
                        console.log('Notification properties:', {
                            isDismissedMoment: notification.isDismissedMoment?.(),
                            getDismissedMomentReason: notification.getDismissedMomentReason?.(),
                            // Try to access any other properties that might exist
                            keys: Object.keys(notification),
                        });
                    }
                });

                console.log('One Tap prompt called successfully');

                // Check if callback was called (indicates prompt might have issues)
                setTimeout(() => {
                    if (!promptCallbackCalled) {
                        console.warn('One Tap prompt callback not called - prompt may not be displaying');
                        console.warn('This could be due to:');
                        console.warn('1. User already signed in to Google');
                        console.warn('2. Browser not supporting FedCM');
                        console.warn('3. One Tap blocked by browser settings');
                        console.warn('4. Client ID configuration issue');
                    }
                }, 2000);

                initializedRef.current = true;
            } catch (error) {
                console.error('Failed to initialize Google One Tap:', error);
                onError?.(error);
            }
        };

        initializeOneTap();

        return () => {
            const googleOneTap = getGoogleOneTap();
            if (googleOneTap && initializedRef.current) {
                try {
                    googleOneTap.cancel();
                } catch (error) {
                    console.warn('Error canceling One Tap:', error);
                }
            }
        };
    }, [clientId, disabled, handleCredentialResponse, onError]);

    return {
        isAvailable: isAvailable && isGoogleOneTapAvailable(),
    };
}
